import React from "react";
import { styled } from "@linaria/react";
import Legend, { defaultRenderSymbol, Label, LegendItem } from "@ui/Highcharts/ui/Legend";
import Popper from "@ui/Popper";
import { color } from "@ui";
import classNames from "classnames";
import { INTERNAL_GAP_ID } from "../../series/getInternalGapSerie";
import { CONFIDENCE_AREA_ID } from "../../series/getConfidenceAreaSerie";
import { QUANTITY_GAP_SERIE_ID } from "../../../Diagnosis/Symptoms/getQuantityGapsSerie";
import { useManualFailures } from "../../../../../api/useManualFailures";
import { css } from "@linaria/core";
import { spacings } from "@ui/Theme";
import LegendTooltip from "../FailureLegend/Tooltip";
import FailureTooltip from "../FailureLegend/FailureTooltip";
import type { FailureLegendProps } from "../FailureLegend";
import { FAULT_IMPACT_CHART } from "../../../Events/views/Event/components/Tabs/DiagnosisTab/FailureEvolution/hook/useFaultGraphOptions";
import { DisplayAllFaultsSwitch } from "../../../Events/components/Switches/DisplayAllFaultsSwitch";
import {
  CLASSIC_FAILURE,
  MANUAL_FAILURE,
  QUANTITY_UNEXPLAINED_GAP_ID,
  UNEXPLAINED_GAP_ID,
} from "../../series/serieIds";

interface P<T extends Highcharts.SeriesOptionsType> extends FailureLegendProps<T> {
  chartType: string;
  displayAllFaults: boolean;
  onChangeDisplayAllFaults: () => void;
}

function FaultEventLegend<T extends Highcharts.SeriesOptionsType>({
  chartRefs,
  series,
  project,
  period,
  setup,
  hideColorPicker,
  onLegendItemClick,
  renderSymbol = defaultRenderSymbol,
  renderFailureActions,
  renderOtherSeriesTooltip,
  renderLabel = (_failure, serie) => <Label>{serie.name}</Label>,
  getRenderColorItemContent,
  chartType,
  displayAllFaults,
  onChangeDisplayAllFaults,
}: P<T>) {
  const firstFailureName = series.find(serie => {
    if (!serie.id) throw new Error("serie should always have an id");
    return serie.id.includes(CLASSIC_FAILURE);
  })?.name;

  const firstManualFailureName = series.find(serie => {
    if (!serie.id) throw new Error("serie should always have an id");
    return serie.id.includes(MANUAL_FAILURE);
  })?.name;

  const manualFailures = useManualFailures(project.id);

  const isOtherSerie = (serieId: string): boolean => {
    const otherSerieIds = [
      INTERNAL_GAP_ID,
      UNEXPLAINED_GAP_ID,
      QUANTITY_UNEXPLAINED_GAP_ID,
      QUANTITY_GAP_SERIE_ID,
      CONFIDENCE_AREA_ID,
    ];

    return otherSerieIds.includes(serieId);
  };
  const hasOtherSeries = series.some(serie => isOtherSerie(serie.id!));

  const renderItem = React.useCallback(
    (serie: T) => {
      if (typeof serie.name !== "string") throw new Error("serie.name should be string");

      const failure =
        setup?.failures.find(f => f.name === serie.name) ||
        manualFailures.find(mf => mf.name === serie.name);

      const symbol = renderSymbol(serie);
      const renderColorItemContent =
        getRenderColorItemContent && failure
          ? getRenderColorItemContent(failure, serie)
          : undefined;

      return (
        <StyledLegendItem
          className={classNames(
            { [FIRST_FAILURE]: hasOtherSeries && serie.name === firstFailureName },
            { [FIRST_MANUAL_FAILURE]: serie.name === firstManualFailureName },
          )}
        >
          <FailurePopper
            hover
            keepOpenOnOverlayActive
            overlay={
              isOtherSerie(serie.id!) ? (
                <LegendTooltip serieName={serie.name} symbol={null}>
                  {renderOtherSeriesTooltip(serie)}
                </LegendTooltip>
              ) : (
                <FailureTooltip
                  failureName={serie.name}
                  failure={failure}
                  renderFailureActions={renderFailureActions}
                  project={project}
                  symbol={symbol}
                  renderColorItemContent={renderColorItemContent}
                  hideColorPicker={hideColorPicker}
                  hideFaultActions
                />
              )
            }
            groupId="Failures"
            openDelay={1000}
            closeDelay={200}
            openGroupDelay={300}
            gap={0}
            placement="right-start"
          >
            <div className={legendItemContent}>
              {renderSymbol(serie)}
              {renderLabel(failure, serie)}
            </div>
          </FailurePopper>
        </StyledLegendItem>
      );
    },
    [
      hasOtherSeries,
      firstFailureName,
      renderSymbol,
      renderLabel,
      renderFailureActions,
      project,
      setup,
      manualFailures,
      firstManualFailureName,
      hideColorPicker,
      getRenderColorItemContent,
      renderOtherSeriesTooltip,
    ],
  );

  return (
    <div className={legendWrapper}>
      <div className={switchWrapper}>
        {chartType === FAULT_IMPACT_CHART ? (
          <DisplayAllFaultsSwitch
            checked={displayAllFaults}
            onChange={onChangeDisplayAllFaults}
            disabled={false}
          />
        ) : null}
      </div>
      <Legend
        chartRefs={chartRefs}
        series={series}
        onLegendItemClick={onLegendItemClick}
        period={period}
        renderItem={renderItem}
      />
    </div>
  );
}

export default React.memo(FaultEventLegend) as typeof FaultEventLegend;

//#region CSS
const legendWrapper = css`
  position: relative;
  display: flex;
  flex-direction: column;
  gap: ${spacings.m};

  & > :last-child {
    flex-grow: 1;
  }
`;
const switchWrapper = css`
  flex: 1;

  & > * + * {
    margin-top: ${spacings.xs};
  }
`;

const FIRST_FAILURE = "firstFailure";
const FIRST_MANUAL_FAILURE = "firstManualFailure";

const StyledLegendItem = styled(LegendItem)`
  &.${FIRST_FAILURE} {
    margin-top: 16px;
  }
  &.${FIRST_MANUAL_FAILURE} {
    margin-top: 16px;
  }
`;

const FailurePopper = styled(Popper)`
  border-radius: 0px 4px 4px 4px;
  background-color: ${color.hydrogen};
  color: ${color.carbon800};
  padding: 12px;
  min-width: 100px;
  max-width: 340px;
  box-shadow: 0px 7px 6px #00000029;
  cursor: auto;
  white-space: normal;
`;

const legendItemContent = css`
  width: 100%;
  display: flex;
  gap: 10px;
  align-items: center;
`;
//#endregion CSS
